import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#27272a] text-white placeholder:text-[#52525b] font-mono text-sm px-4 py-3.5 focus:outline-none focus:border-[#dc2626] transition-colors";

const QuoteForm = () => {
  const { t } = useLang();
  const services = t("form.services");

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: services[0],
    message: "",
    inquiry_type: "b2b",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error(t("form.error"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t("form.success"));
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: services[0],
        message: "",
        inquiry_type: form.inquiry_type,
      });
    } catch (err) {
      toast.error(t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="quote-form-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-[#1f1f1f]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading + contact */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#dc2626]" />
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#dc2626]">
                {t("form.tag")}
              </p>
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl lg:text-6xl leading-[0.95] mb-6">
              {t("form.heading")}
            </h2>
            <p className="text-base text-[#a1a1aa] leading-relaxed mb-10">
              {t("form.lead")}
            </p>

            <div className="space-y-4">
              <ContactRow
                icon={<Phone className="w-4 h-4" />}
                label="01"
                href="tel:+48513066040"
                text={t("contact.phone")}
              />
              <ContactRow
                icon={<MapPin className="w-4 h-4" />}
                label="02"
                href="https://www.google.com/maps/place/D%C5%82uga+9,+05-100+Nowy+Dw%C3%B3r+Mazowiecki"
                text={t("contact.address")}
                external
              />
              <ContactRow
                icon={<Clock className="w-4 h-4" />}
                label="03"
                text={`${t("contact.hours")} · ${t("contact.hoursWeekend")}`}
              />
              <ContactRow
                icon={<Mail className="w-4 h-4" />}
                label="04"
                href="mailto:biuro@dobralakiernia.pl"
                text="biuro@dobralakiernia.pl"
              />
            </div>

            {/* Map embed */}
            <div className="mt-10 border border-[#27272a] aspect-[4/3] overflow-hidden">
              <iframe
                title={t("contact.mapTitle")}
                src="https://www.google.com/maps?q=D%C5%82uga+9,+05-100+Nowy+Dw%C3%B3r+Mazowiecki&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="bg-[#0e0e0e] border border-[#1f1f1f] p-6 md:p-10"
            >
              {/* Type tabs */}
              <div className="grid grid-cols-2 gap-px bg-[#1f1f1f] mb-8">
                {[
                  { v: "b2b", label: t("form.type.b2b") },
                  { v: "retail", label: t("form.type.retail") },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`form-type-${opt.v}`}
                    onClick={() =>
                      setForm((p) => ({ ...p, inquiry_type: opt.v }))
                    }
                    className={`py-4 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors ${
                      form.inquiry_type === opt.v
                        ? "bg-[#dc2626] text-white font-semibold"
                        : "bg-[#0a0a0a] text-[#a1a1aa] hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FormField label={t("form.name")} required>
                  <input
                    data-testid="form-name"
                    className={inputCls}
                    value={form.name}
                    onChange={set("name")}
                    required
                  />
                </FormField>
                <FormField label={t("form.company")}>
                  <input
                    data-testid="form-company"
                    className={inputCls}
                    value={form.company}
                    onChange={set("company")}
                  />
                </FormField>
                <FormField label={t("form.email")} required>
                  <input
                    type="email"
                    data-testid="form-email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    required
                  />
                </FormField>
                <FormField label={t("form.phone")}>
                  <input
                    type="tel"
                    data-testid="form-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </FormField>
                <div className="sm:col-span-2">
                  <FormField label={t("form.service")}>
                    <Select
                      value={form.service}
                      onValueChange={(v) =>
                        setForm((p) => ({ ...p, service: v }))
                      }
                    >
                      <SelectTrigger
                        data-testid="form-service"
                        className="bg-[#0a0a0a] border-[#27272a] rounded-none h-[52px] font-mono text-sm text-white focus:ring-0 focus:border-[#dc2626]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#27272a] rounded-none text-white">
                        {services.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            className="rounded-none font-mono text-sm focus:bg-[#dc2626] focus:text-white"
                          >
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
                <div className="sm:col-span-2">
                  <FormField label={t("form.message")} required>
                    <textarea
                      data-testid="form-message"
                      rows={5}
                      placeholder={t("form.placeholderMessage")}
                      className={`${inputCls} resize-none`}
                      value={form.message}
                      onChange={set("message")}
                      required
                    />
                  </FormField>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#52525b]">
                  {t("form.altCall")} · +48 513 066 040
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <a
                    href="tel:+48513066040"
                    data-testid="form-call-now"
                    className="group inline-flex items-center justify-center gap-2 border-2 border-[#dc2626] text-[#dc2626] font-bold uppercase tracking-wider text-xs px-8 py-4 hover:bg-[#dc2626] hover:text-white transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5 group-hover:animate-pulse" />
                    {t("form.callNow")}
                  </a>
                  <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 bg-[#dc2626] text-white font-bold uppercase tracking-wider text-xs px-8 py-4 hover:bg-[#b91c1c] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? t("form.sending") : t("form.submit")}
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#a1a1aa] mb-2 inline-block">
      {label}
      {required && <span className="text-[#dc2626] ml-1">*</span>}
    </span>
    {children}
  </label>
);

const ContactRow = ({ icon, label, href, text, external }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      {...(href ? { href, ...(external ? { target: "_blank", rel: "noopener noreferrer" } : {}) } : {})}
      className="flex items-center gap-4 border-b border-[#1f1f1f] pb-4 group"
    >
      <span className="font-mono text-[10px] text-[#52525b] tracking-[0.2em]">
        {label}
      </span>
      <span className="text-[#dc2626]">{icon}</span>
      <span className="text-sm text-white group-hover:text-[#dc2626] transition-colors">
        {text}
      </span>
    </Tag>
  );
};

export default QuoteForm;
